'use client';

import useSWR from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const useTireHistoricalList = (
  page,
  rowsPerPage,
  searchField,
  searchValue,
  selectedCompanies = [],
  dateRange = null,
  selectedSerialNumbers = []
) => {
  const start = page * rowsPerPage;
  const companies = (selectedCompanies || []).map((c) => c.value).join(',');
  const serialNumbers = (selectedSerialNumbers || []).map((s) => s.value).join(', ');
  const payload = useMemo(() => {
    const fb = new FilterBuilder().setPaging(start, rowsPerPage).setOrder(['created_at'], false);

    if (dateRange && dateRange.startDate && dateRange.endDate) {
      fb.where('RANGE', 'transaction_date', dateRange.startDate, 'AND', dateRange.endDate);
    }

    // Add text search if available
    if (searchField && searchValue) {
      if (searchField === 'all') {
        fb.like('license_plate', searchValue, 'OR');
        fb.like('serial_number', searchValue, 'OR');
      } else {
        fb.like(searchField, searchValue, 'AND');
      }
    }

    // Add company filter
    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    } else {
      fb.filterByCompany();
    }

    if (serialNumbers) {
      if (serialNumbers.includes(',')) {
        fb.in('serial_number', serialNumbers);
      } else {
        fb.equal('serial_number', serialNumbers);
      }
    }

    return fb.build();
  }, [start, rowsPerPage, searchField, searchValue, companies, dateRange, serialNumbers]);

  const key = useMemo(() => ['/tire-transactions/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  return {
    data: data?.data?.data || data?.data || [],
    totalRecord: data?.data?.totalRecord || data?.totalRecord || 0,
    error,
    isLoading
  };
};

export const exportTireHistoricalList = async (
  searchField,
  searchValue,
  selectedCompanies = [],
  totalRecords,
  dateRange = null,
  selectedSerialNumbers = []
) => {
  try {
    const length = totalRecords > 0 ? totalRecords : 9999;
    const fb = new FilterBuilder().setPaging(0, length).setOrder(['created_at'], false);

    if (dateRange && dateRange.startDate && dateRange.endDate) {
      fb.where('RANGE', 'transaction_date', dateRange.startDate, 'AND', dateRange.endDate);
    }

    if (searchField && searchValue) {
      if (searchField === 'all') {
        fb.like('license_plate', searchValue, 'OR');
        fb.like('serial_number', searchValue, 'OR');
      } else {
        fb.like(searchField, searchValue, 'AND');
      }
    }

    const companies = (selectedCompanies || []).map((c) => c.value).join(',');
    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    } else {
      fb.filterByCompany();
    }

    const serialNumbers = (selectedSerialNumbers || []).map((s) => s.value).join(', ');
    if (serialNumbers) {
      if (serialNumbers.includes(',')) {
        fb.in('serial_number', serialNumbers);
      } else {
        fb.equal('serial_number', serialNumbers);
      }
    }

    const response = await fleetApi.post(`/tire-transactions/search${getTzParam()}`, fb.build());
    return response?.data?.data || response?.data || [];
  } catch (err) {
    console.error('Failed to export tire historical', err);
    return [];
  }
};
